import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Users,
  UserPlus,
  LogOut,
  ArrowLeft,
} from "lucide-react";
import { AdminAuthProvider, useAdminAuth } from "@/admin/AdminAuthContext";
import AdminLogin from "@/admin/pages/AdminLogin";
import { BRAND } from "@/lib/content";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/posts", label: "Insights", icon: FileText },
  { to: "/admin/leads", label: "Leads", icon: Users },
  { to: "/admin/applications", label: "Aplicaciones", icon: UserPlus },
];

const Shell = () => {
  const { isAuthenticated, logout } = useAdminAuth();
  const navigate = useNavigate();

  if (!isAuthenticated) return <AdminLogin />;

  const handleLogout = () => {
    logout();
    navigate("/admin");
  };

  return (
    <div
      data-testid="admin-shell"
      className="min-h-screen bg-[#0B0410] text-[#F9F6F0] font-sans-emb flex"
    >
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-white/10 bg-[#0E0518] sticky top-0 h-screen">
        <Link
          to="/admin"
          className="flex items-center gap-3 px-6 py-6 border-b border-white/10"
          data-testid="admin-logo-link"
        >
          <img
            src={BRAND.logoWhite}
            alt=""
            className="h-9 w-9 object-contain"
          />
          <div className="leading-tight">
            <div className="font-display text-base">EMB</div>
            <div className="text-[9px] tracking-luxury uppercase text-[#A397B4]">
              Panel admin
            </div>
          </div>
        </Link>

        <nav className="flex-1 px-3 py-5">
          <ul className="flex flex-col gap-1">
            {NAV.map((n) => (
              <li key={n.to}>
                <NavLink
                  to={n.to}
                  end={n.end}
                  data-testid={`admin-nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                      isActive
                        ? "bg-[#3D1565]/40 text-[#F9F6F0] border-l-2 border-[#E6D5B8]"
                        : "text-[#F9F6F0]/65 hover:text-[#F9F6F0] hover:bg-white/[0.03]"
                    }`
                  }
                >
                  <n.icon size={16} strokeWidth={1.6} />
                  <span>{n.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="px-4 py-4 border-t border-white/10 flex flex-col gap-2">
          <Link
            to="/"
            data-testid="admin-back-to-site"
            className="flex items-center gap-2 text-[11px] tracking-luxury uppercase text-[#F9F6F0]/55 hover:text-[#E6D5B8] transition-colors px-2 py-2"
          >
            <ArrowLeft size={13} />
            <span>Volver al sitio</span>
          </Link>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-2 text-[11px] tracking-luxury uppercase text-[#F9F6F0]/65 hover:text-red-300 transition-colors px-2 py-2 text-left"
          >
            <LogOut size={13} />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </aside>

      {/* Mobile top bar (since sidebar is hidden on mobile) */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#0E0518] border-b border-white/10 px-5 h-14 flex items-center justify-between">
        <Link to="/admin" className="flex items-center gap-2">
          <img src={BRAND.logoWhite} alt="" className="h-7 w-7" />
          <span className="font-display text-sm">EMB · Admin</span>
        </Link>
        <button
          onClick={handleLogout}
          className="text-[10px] tracking-luxury uppercase text-[#F9F6F0]/65"
        >
          Salir
        </button>
      </div>

      {/* Mobile bottom tab bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#0E0518] border-t border-white/10 grid grid-cols-4">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 py-2.5 text-[10px] tracking-wide ${
                isActive
                  ? "text-[#E6D5B8]"
                  : "text-[#F9F6F0]/55"
              }`
            }
          >
            <n.icon size={16} strokeWidth={1.6} />
            <span>{n.label}</span>
          </NavLink>
        ))}
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 lg:py-10 py-20 pb-24 lg:pb-10 px-5 lg:px-10">
        <Outlet />
      </main>
    </div>
  );
};

const AdminLayout = () => (
  <AdminAuthProvider>
    <Shell />
  </AdminAuthProvider>
);

export default AdminLayout;
